/**
 * Grand Quack Auto — physics system.
 *
 * Initializes a cannon-es World with ground plane and building collision
 * bodies. Steps physics each fixedUpdate. Syncs body transforms to Three.js
 * meshes in render. Exports addPhysicsBody() for other systems to register
 * dynamic body→mesh pairs.
 *
 * See research.md §3 performance budget and §5 .cursorrules "Physics".
 */

import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { DUCK, FIXED_DT, PHYSICS } from '../config';
import type { System, World } from '../types';
import { pushOutOfBuildings } from '../utils/buildingCollision';

let initialized = false;

// Reusable objects for InstancedMesh decomposition.
const _mat4 = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scl = new THREE.Vector3();

/** Initialize the cannon-es world. Must be called before creating cars. */
export function initPhysics(world: World): void {
  initialized = true;

  const pw = new CANNON.World();
  pw.gravity.set(0, PHYSICS.gravity, 0);
  pw.broadphase = new CANNON.SAPBroadphase(pw);
  (pw.solver as CANNON.GSSolver).iterations = PHYSICS.solverIterations;

  // Ground plane — static, rotated so normal faces +Y.
  const ground = new CANNON.Body({
    type: CANNON.Body.STATIC,
    shape: new CANNON.Plane(),
  });
  ground.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
  pw.addBody(ground);

  // Building collision bodies — iterate the InstancedMesh instances.
  const buildingMesh = world.scene.getObjectByName('buildings') as THREE.InstancedMesh | null;
  if (buildingMesh) {
    const shrink = PHYSICS.buildingColliderShrink;
    for (let i = 0; i < buildingMesh.count; i++) {
      buildingMesh.getMatrixAt(i, _mat4);
      _mat4.decompose(_pos, _quat, _scl);
      const body = new CANNON.Body({
        type: CANNON.Body.STATIC,
        shape: new CANNON.Box(new CANNON.Vec3(_scl.x / 2 * shrink, _scl.y / 2, _scl.z / 2 * shrink)),
      });
      body.position.set(_pos.x, _pos.y, _pos.z);
      pw.addBody(body);
    }
  }

  // Duck body — dynamic sphere. Movement system drives velocity; mesh sync
  // below copies x/z to world.duck but leaves y alone (visual duck sits on 0).
  const duckBody = new CANNON.Body({
    mass: DUCK.bodyMass,
    shape: new CANNON.Sphere(DUCK.bodyRadius),
    linearDamping: DUCK.linearDamping,
  });
  duckBody.position.set(world.duck.position.x, DUCK.bodyRadius, world.duck.position.z);
  duckBody.angularFactor.set(0, 0, 0); // lock rotation — mesh yaw is independent
  pw.addBody(duckBody);

  world.physics = { world: pw, bodies: [], duckBody };
}

/**
 * Register a dynamic body→mesh pair for automatic sync in render.
 * Call this after creating the body — it adds the body to the CANNON world.
 */
export function addPhysicsBody(world: World, body: CANNON.Body, mesh: THREE.Object3D): void {
  if (!world.physics) return;
  world.physics.world.addBody(body);
  world.physics.bodies.push({ body, mesh });
}

export const physicsSystem: System = {
  name: 'physics',

  fixedUpdate(dt: number, world: World) {
    if (!initialized) initPhysics(world);
    if (!world.physics) return;
    // Sub-step at 1/120 s (3 substeps per gameplay tick) so a fast car can't
    // skip across a whole wall thickness in one step. cannon-es has no CCD,
    // this is the substitute.
    world.physics.world.step(1 / 120, dt, 3);
  },

  render(_dt: number, alpha: number, world: World) {
    if (!world.physics) return;
    for (const { body, mesh } of world.physics.bodies) {
      mesh.position.set(body.position.x, body.position.y, body.position.z);
      mesh.quaternion.set(
        body.quaternion.x, body.quaternion.y,
        body.quaternion.z, body.quaternion.w,
      );
    }
    // Duck: copy x/z only. Y and rotation are owned by movement/animation so
    // the visual duck stays grounded at y≈0 while the body center sits at
    // y=bodyRadius. Skipped while driving — vehicleSystem hides the duck.
    // Extrapolate past the last physics tick by the fractional remainder
    // (alpha * FIXED_DT) so the duck is buttery smooth on 120 Hz+ displays
    // instead of snapping to 60 Hz body positions.
    const duckBody = world.physics.duckBody;
    if (duckBody && world.playerMode === 'foot') {
      const rawX = duckBody.position.x + duckBody.velocity.x * alpha * FIXED_DT;
      const rawZ = duckBody.position.z + duckBody.velocity.z * alpha * FIXED_DT;
      // Safety net: push duck out of buildings if cannon-es tunneling occurs.
      const fixed = pushOutOfBuildings(rawX, rawZ);
      world.duck.position.x = fixed.x;
      world.duck.position.z = fixed.z;
      // Sync body back if corrected to prevent repeated tunneling.
      if (fixed.x !== rawX || fixed.z !== rawZ) {
        duckBody.position.x = fixed.x;
        duckBody.position.z = fixed.z;
        duckBody.velocity.x = 0;
        duckBody.velocity.z = 0;
      }
    }
  },
};
